import { graphql, useFragment } from 'react-relay';

import {
    PricingGuidancePriceRange,
    pricingGuidanceHelpers_item$key,
    pricingGuidanceHelpers_item$data,
} from './__generated__/pricingGuidanceHelpers_item.graphql';

const priceRanges: PricingGuidancePriceRange[] = [
    'OUT_OF_RANGE_LOW',
    'LOWER_RANGE',
    'NEAR_MEDIAN_LOW',
    'MEDIAN',
    'NEAR_MEDIAN_HIGH',
    'HIGHER_RANGE',
    'OUT_OF_RANGE_HIGH',
];

const itemFragment = graphql`
    fragment pricingGuidanceHelpers_item on Item {
        pricingGuidance(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) {
            priceRange
            price {
                convertedAmountList {
                    amount
                    currency
                }
            }
            minAmount {
                convertedAmountList {
                    amount
                    currency
                }
            }
            medianAmount {
                convertedAmountList {
                    amount
                    currency
                }
            }
            maxAmount {
                convertedAmountList {
                    amount
                    currency
                }
            }
        }
        displayPrice(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) {
            convertedAmountList {
                amount
                currency
            }
        }
    }
`;

const getPriceAmountByCurrency = ({
    price,
    currency,
}: {
    price?: NonNullable<pricingGuidanceHelpers_item$data['pricingGuidance']>['price'];
    currency: string;
}): number => {
    const priceAmount = (price?.convertedAmountList || []).find(
        convertedAmount => convertedAmount?.currency === currency
    );

    return Math.floor(priceAmount?.amount || 0);
};

export const useGetPriceAmountByType = ({
    item: itemRef,
    currency,
    type,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
    type: 'MIN' | 'MEDIAN' | 'MAX';
}): number => {
    const item = useFragment(itemFragment, itemRef);
    const { pricingGuidance } = item || {};
    const { minAmount, medianAmount, maxAmount } = pricingGuidance || {};
    let price;

    switch (type) {
        case 'MIN':
            price = minAmount;
            break;
        case 'MEDIAN':
            price = medianAmount;
            break;
        case 'MAX':
            price = maxAmount;
            break;
    }

    return getPriceAmountByCurrency({ price, currency });
};

export const useGetGuidancePriceAmount = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): number => {
    const item = useFragment(itemFragment, itemRef);
    const { pricingGuidance, displayPrice } = item || {};
    const { price } = pricingGuidance || {};

    return getPriceAmountByCurrency({ price: price || displayPrice?.[0], currency });
};

export function useIsPriceRangeOrHigher({
    item: itemRef,
    targetPriceRange,
}: {
    item: pricingGuidanceHelpers_item$key;
    targetPriceRange: PricingGuidancePriceRange;
}): boolean {
    const item = useFragment(itemFragment, itemRef);
    const { priceRange } = item?.pricingGuidance || {};

    return !!priceRange && priceRanges.indexOf(priceRange) >= priceRanges.indexOf(targetPriceRange);
}
